/**
 * Created by Bart Decorte
 * Date: 22/02/2020
 * Time: 12:42
 */
const EventEmitter = require('events');
import Request from '../../utils/Request';
import {readAsBinaryString} from '../../helpers/blob_helper';

export default class ChunkedUpload extends EventEmitter {
  constructor(file, { index = 0, maxConnections = 10 }) {
    super();
    this.file = file;
    this.index = index;
    this.identifier = undefined;
    this.chunkSize = 4 * 1000 * 1000; // Bytes, 4 MB
    this.maxConnections = maxConnections;
    this.activeConnections = 0;
    this.active = false;
    this.canceled = false;
    this.failed = false;
    this.successPropagated = false;
    this.canceledPropagated = false;
    this.failurePropagated = false;

    this.initQueue();
  }

  setMaxConnections(maxConnections) {
    this.maxConnections = maxConnections;
    if (this.active) {
      this.nextChunk();
    }
  }

  chunkCount() {
    if (this.canceled || this.failed) {
      return 0;
    }

    return Math.ceil(this.file.size / this.chunkSize);
  }

  pendingChunkCount() {
    if (this.canceled || this.failed) {
      return 0;
    }

    return this.queue.length;
  }

  activeChunkCount() {
    return this.activeConnections;
  }

  failedChunkTolerationCount() {
    // 10 chunks or 10% of total chunks
    return Math.min(10, Math.floor(this.chunkCount() / 10));
  }

  tooManyFailedChunks() {
    return this.failedChunkCount > this.failedChunkTolerationCount();
  }

  initQueue() {
    this.queue = new Array(this.chunkCount())
      .fill()
      .map((_, index) => index)
      .reverse();
    return this;
  }

  execute() {
    this.active = true;
    return this.getUniqueIdentifier().then(() => {
      this.failedChunkCount = 0;
      this.nextChunk();
    });
  }

  getUniqueIdentifier() {
    const request = new Request('uploads/identifier', 'GET');
    return request.execute().then((response) => {
      this.identifier = response.data.identifier;
      return this.identifier;
    });
  }

  connectionAvailable() {
    return this.activeConnections < this.maxConnections;
  }

  chunk(begin) {
    const chunk = this.file.slice(begin, begin + this.chunkSize);

    return readAsBinaryString(chunk).then((data) => {
      const patternLf = '\n--';
      const chunks = [];
      let begin = 0;
      let end = data.indexOf(patternLf, begin);
      while (end > -1) {
        chunks.push(chunk.slice(begin, end + 2));
        begin = end + 2;
        end = data.indexOf(patternLf, begin);
      }
      chunks.push(chunk.slice(begin, this.chunkSize));
      return chunks;
    });
  }

  uploadChunk(chunk, chunkId) {
    const data = new FormData();
    chunk.forEach((chunkPart, i) => {
      data.append(`chunk[${i}]`, chunkPart);
    });
    const request = new Request('uploads/chunk', 'POST', data);
    return request.execute({
      headers: {
        'X-Chunk-Id': chunkId,
        'X-Chunk-Count': this.chunkCount(),
        'X-Chunk-Size': this.chunkSize,
        'X-Upload-Id': this.identifier,
      },
      processData: false,
      dataType: 'json',
      contentType: false,
    });
  }

  meta() {
    return {
      index: this.index,
      identifier: this.identifier,
      name: this.file.name,
      type: this.file.type,
    };
  }

  nextChunk() {

    // Not yet received an upload identifier from the server
    if (!this.identifier) {
      return;
    }

    // No more queued chunks to upload
    if (!this.queue.length) {
      // No more active connections, looks like we're done
      if (!this.activeConnections) {
        this.active = false;
        this.emitSuccess();
      }
      return;
    }

    if (!this.connectionAvailable()) {
      return;
    }

    // User might've canceled while upload was in progress
    if (this.canceled) {
      this.active = false;
      this.emitCancellation();
      return;
    }

    // We don't want to retry endlessly if too many requests are failing
    if (this.tooManyFailedChunks()) {
      this.active = false;
      this.failed = true;
      this.emitFailure();
      return;
    }

    const chunkId = this.queue.pop();
    const begin = chunkId * this.chunkSize;
    this.activeConnections += 1;

    // Call self for parallel requests
    this.nextChunk();

    return this.chunk(begin)
      .then((chunk) => {
        return this.uploadChunk(chunk, chunkId);
      })
      .catch((error) => {
        this.failedChunkCount += 1;
        this.activeConnections -= 1;
        // push the chunk back on the queue
        this.queue.push(chunkId);
        this.emitProgress();
        this.nextChunk();
      })
      .then((response) => {
        this.activeConnections -= 1;
        this.emitProgress();
        this.nextChunk();
      });
  }

  progress() {
    return 1 - (this.pendingChunkCount() + this.activeChunkCount()) / this.chunkCount();
  }

  emitProgress() {
    if (this.canceled) {
      return;
    }

    // progress: total chunks minus to do minus doing
    this.emit('progress', { progress: this.progress() });
  }

  emitSuccess() {
    if (this.canceled) {
      return;
    }

    // Only emit once, other classes might be keeping count of busy instances
    if (this.successPropagated) {
      return;
    }

    this.successPropagated = true;

    this.emit('success', {
      result: [this.meta()],
    });
  }

  emitCancellation() {
    // Only emit once, other classes might be keeping count of busy instances
    if (this.canceledPropagated) {
      return;
    }
    this.emit('canceled');
    this.canceledPropagated = true;
  }

  emitFailure() {
    // Only emit once, other classes might be keeping count of busy instances
    if (this.failurePropagated) {
      return;
    }
    this.emit('failure');
    this.failurePropagated = true;
  }

  cancel() {
    this.canceled = true;
    if (this.active) {
      this.emitCancellation();
    }
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/components/uploads/ChunkedUpload.js